import type { CatalogComponentProperties } from '@/domain/catalog/common';
import type { CupSystem } from '@/domain/catalog/cup';
import type { LinerSize } from '@/domain/catalog/liner';

export enum BearingInnerDiameter {
    // noinspection JSUnusedGlobalSymbols
    D22 = 22,
    D28 = 28,
}

export enum BearingOuterDiameter {
    // noinspection JSUnusedGlobalSymbols
    D32 = 32,
    D34 = 34,
    D36 = 36,
    D38 = 38,
    D40 = 40,
    D42 = 42,
    D44 = 44,
    D46 = 46,
    D50 = 50,
    D54 = 54,
    D60 = 60,
}

// for now we only use head size 28 for dual mobility bearings
export const bearingInnerDiameters = Object.values(BearingInnerDiameter).filter((value): value is number => typeof value === 'number');
export type BearingInnerDiameterValue = (typeof bearingInnerDiameters)[number];
const bearingInnerDiameterRegex = /(\d+) mm/;

export function isBearingInnerDiameter(value: number): value is BearingInnerDiameterValue {
    return bearingInnerDiameters.includes(value as BearingInnerDiameterValue);
}

export function bearingInnerDiameterFromString(value: string): BearingInnerDiameterValue {
    const match = value.match(bearingInnerDiameterRegex);
    if (!match) {
        throw Error(`Invalid bearing-diameter string '${value}'`);
    }
    const result = parseInt(match[1]);
    if (!isBearingInnerDiameter(result)) {
        throw Error(`Invalid bearing-diameter ${result}`);
    }
    return result;
}

export const bearingOuterDiameters = Object.values(BearingOuterDiameter).filter((value): value is number => typeof value === 'number');
export type BearingOuterDiameterValue = (typeof bearingOuterDiameters)[number];
const bearingOuterDiameterRegex = /(\d+) mm/;

export function isBearingOuterDiameter(value: number): value is BearingOuterDiameterValue {
    return bearingOuterDiameters.includes(value as BearingOuterDiameterValue);
}

export function bearingOuterDiameterFromString(value: string): BearingOuterDiameterValue {
    const match = value.match(bearingOuterDiameterRegex);
    if (!match) {
        throw Error(`Invalid bearing-diameter string '${value}'`);
    }
    const result = parseInt(match[1]);
    if (!isBearingOuterDiameter(result)) {
        throw Error(`Invalid bearing-diameter ${result}`);
    }
    return result;
}

/** Properties of a type of bearing, independent of its use in any particular case  */
export type CatalogBearing = CatalogComponentProperties & {
    /** The cup-system the bearing belongs to e.g. g7 */
    system: CupSystem;

    /** The size of the liner A, B, C... */
    linerSize: LinerSize;

    /** The inner diameter of the bearing */
    innerDiameter: BearingInnerDiameterValue;

    /** The outer diameter of the bearing */
    outerDiameter: BearingOuterDiameterValue;
};
